import { AxiosError } from 'axios';
import { useCheckoutWizard } from './useCheckoutWizard';
import {
  EnrollmentEvent,
  type CheckoutPaymentFailureStage,
} from '@/constants/analytics';
import { useNotification } from '@/context/notification';
import { useCatalogOfferCheckout } from '@/data/catalog/offer';
import { PaymentMethod } from '@/data/catalog/offer/interfaces';
import { useCreateRegistration } from '@/data/courses/registration';
import { usePagarmeCardToken } from '@/data/pagarme';
import type { RegistrationFormData } from '@/packages/courses/components/user-data-form/schema';
import { CheckoutStepIds } from '@/packages/courses/pages/checkout/types';
import { buildCheckoutPayload } from '@/packages/courses/pages/checkout/utils/build-checkout-payload';
import { buildRegistrationPayload } from '@/packages/courses/pages/checkout/utils/build-registration-payload';
import {
  redactCheckoutPayloadData,
  redactRegistrartionPayloadData,
} from '@/packages/courses/pages/checkout/utils/redact-payload-data';
import {
  handleCheckoutError,
  handleSubscriptionError,
} from '@/packages/courses/utils/error-message-handlers';
import { buildOfferEnrollmentProperties } from '@/packages/courses/utils/enrollment-analytics';
import { track } from '@/utils/analytics';
import { captureException } from '@/utils/capture-exception';

export const usePayment = () => {
  const { toast } = useNotification();
  const {
    actions,
    offer,
    subscriptionId,
    paymentMethod,
    customerInfo,
    creditCardData,
    installmentsData,
    coupon,
  } = useCheckoutWizard();
  const { getCardToken, isPending: isGettingCardToken } = usePagarmeCardToken();
  const { mutate: createRegistration, isPending: isCreatingRegistration } =
    useCreateRegistration();
  const offerId = offer?.id ?? 0;
  const { checkout, isPending: isProcessingCheckout } =
    useCatalogOfferCheckout(offerId);

  const isLoading =
    isProcessingCheckout || isGettingCardToken || isCreatingRegistration;

  const enrollmentProperties = buildOfferEnrollmentProperties(offer);

  const trackPaymentFailed = (
    stage: CheckoutPaymentFailureStage,
    errorReason: string
  ) =>
    track(EnrollmentEvent.CheckoutPaymentFailed, {
      ...enrollmentProperties,
      payment_method: paymentMethod,
      stage,
      error_reason: errorReason,
    });

  const handleRegistration = (data: RegistrationFormData) => {
    if (!offer?.id) return;
    const payload = buildRegistrationPayload(data, coupon?.code);
    createRegistration(
      { courseId: offer.id, body: payload },
      {
        onSuccess: (response) => {
          track(EnrollmentEvent.CheckoutCustomerInfoSubmitted, {
            ...enrollmentProperties,
            success: true,
          });
          if (response?.data?.id) {
            actions.updateValues({ subscriptionId: response.data.id });
          }
          actions.navigate(CheckoutStepIds.PaymentMethod);
        },
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<{ data: { message: string } }>;
          const { errorMessage, isUnknownError } =
            handleSubscriptionError(axiosError);

          if (isUnknownError) {
            captureException({
              error,
              redactedData: redactRegistrartionPayloadData(payload),
            });
          }

          track(EnrollmentEvent.CheckoutCustomerInfoSubmitted, {
            ...enrollmentProperties,
            success: false,
            error_reason: errorMessage,
          });

          toast({
            title: 'Erro ao submeter inscrição',
            content: errorMessage,
            type: 'error',
          });
        },
      }
    );
  };

  const handlePixBankSlip = () => {
    if (!subscriptionId || !customerInfo || !paymentMethod) return;
    const payload = buildCheckoutPayload({
      customerInfo,
      paymentMethod,
      subscriptionId,
      coupon: coupon?.code,
    });

    checkout(payload, {
      onSuccess: ({ data: { orderId, paymentData } }) => {
        actions.updateValues({
          [CheckoutStepIds.PixBankSlip]: {
            pixBoletoGenerated: true,
            paymentData,
          },
        });
        actions.updateValues({ orderId });
        actions.navigate(CheckoutStepIds.PixBankSlip);
      },
      onError: (error) => {
        const axiosError = error as AxiosError<{ data: { message: string } }>;
        const { errorMessage, isUnknownError } =
          handleCheckoutError(axiosError);
        if (isUnknownError) {
          captureException({
            error,
            redactedData: redactCheckoutPayloadData(payload),
          });
        }
        trackPaymentFailed('checkout_request', errorMessage);
        toast({
          title: 'Erro ao processar pagamento',
          content: errorMessage,
          type: 'error',
        });
      },
    });
  };

  const handleCreditCard = () => {
    const isInvalidData =
      !creditCardData ||
      !subscriptionId ||
      !offer ||
      !customerInfo ||
      !paymentMethod;
    if (isInvalidData) return;

    const [exp_month, exp_year] = creditCardData.expiration.split('/');
    getCardToken(
      {
        number: creditCardData.number.replace(/\s/g, ''),
        holder_name: creditCardData.holder_name,
        exp_month,
        exp_year,
        cvv: creditCardData.cvv,
      },
      {
        onSuccess: (cardToken) => {
          const payload = buildCheckoutPayload({
            amount: offer.sale_price,
            customerInfo,
            paymentMethod,
            subscriptionId,
            creditCardData,
            installmentsData,
            idToken: cardToken.id,
            coupon: coupon?.code,
          });

          checkout(payload, {
            onSuccess: ({ data: { orderId } }) => {
              actions.updateValues({ orderId });
              actions.navigate(CheckoutStepIds.PaymentStatus);
            },
            onError: (error) => {
              const errorMessage =
                'Ocorreu um erro ao processar o pagamento com cartão de crédito';

              trackPaymentFailed('checkout_request', errorMessage);
              toast({
                title: 'Erro ao processar pagamento',
                content: errorMessage,
                type: 'error',
              });
              captureException({
                error,
                redactedData: redactCheckoutPayloadData(payload),
              });
            },
          });
        },
        onError: (error) => {
          const errorMessage = 'Confirme os dados do cartão';
          trackPaymentFailed('card_validation', errorMessage);
          toast({
            title: 'Cartão inválido',
            content: errorMessage,
            type: 'error',
          });
          captureException({ error, sensitive: true });
        },
      }
    );
  };

  const handlePaymentProcess = () => {
    track(EnrollmentEvent.CheckoutPaymentSubmitted, {
      ...enrollmentProperties,
      payment_method: paymentMethod,
      installments: installmentsData?.installments,
      amount: offer?.checkout_pricing?.calculated_amount,
      has_coupon: Boolean(coupon?.code),
    });

    const isPixOrBankSlip =
      paymentMethod === PaymentMethod.Pix ||
      paymentMethod === PaymentMethod.BankSlip;

    if (isPixOrBankSlip) {
      handlePixBankSlip();
      return;
    }

    if (paymentMethod === PaymentMethod.Card) {
      handleCreditCard();
      return;
    }
  };

  return {
    handlePaymentProcess,
    handleRegistration,
    isLoading,
  };
};
